import { useState, useEffect, useRef } from 'react';
import { Target, Heart, X, ChevronRight } from 'lucide-react';
import { EnhancedUserProfile, EnhancedRecommendationResponse, RunningShoe, EnhancedRecommendation } from '../types';
import { enhancedFormSteps } from '../data/enhancedFormSteps';
import { useAuth } from '../contexts/AuthContext';
import PaymentModal from './PaymentModal';
import QuickAuthModal from './QuickAuthModal';
import PremiumUpgradeModal from './PremiumUpgradeModal';
import RunningShoeLoader from './RunningShoeLoader';
import BrandFilterModal from './BrandFilterModal';
import api, { addFavorite, removeFavorite, checkFavoriteStatus, searchShoes, checkBuyNowPermission } from '../utils/api';
import toast from 'react-hot-toast';
import useRecommendationState from '../hooks/useRecommendationState';
import { useLocation, useNavigate } from 'react-router-dom';

interface RecommendationCardProps {
  recommendation: any;
  isLarge?: boolean;
  rank: number;
  onBuyClick: (shoe: any) => void;
  onFavoriteClick: (shoe: any) => void;
  isFavorite?: boolean;
  hasBuyNowPermission?: boolean;
}

interface BuyButtonProps {
  shoe: any;
  onClick: (shoe: any) => void;
  size?: 'small' | 'medium';
  hasPermission?: boolean;
}

const BuyButton: React.FC<BuyButtonProps> = ({ shoe, onClick, size = 'medium', hasPermission = false }) => {
  const buttonClasses = size === 'small'
    ? "bg-orange-600 hover:bg-orange-700 active:bg-orange-800 focus:bg-orange-700 text-white text-[10px] sm:text-xs font-semibold py-1.5 sm:py-2 px-2 sm:px-3 rounded sm:rounded-lg transition-colors duration-200 text-center cursor-pointer w-full flex-shrink-0"
    : "bg-orange-600 hover:bg-orange-700 active:bg-orange-800 focus:bg-orange-700 text-white font-semibold py-2 sm:py-3 px-3 sm:px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 text-xs sm:text-sm cursor-pointer w-full flex-shrink-0";

  // Check if purchase link is valid
  const hasValidPurchaseLink = shoe.purchaseLink && shoe.purchaseLink !== '' && shoe.purchaseLink !== '#';

  // Only render as <a> tag if user has permission AND purchaseLink is valid
  if (hasPermission && hasValidPurchaseLink) {
    return (
      <a
        href={shoe.purchaseLink}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonClasses}
        style={{ display: 'flex', textDecoration: 'none' }}
      >
        {size === 'medium' && <span>Buy Now</span>}
        {size === 'small' && 'Buy Now'}
      </a>
    );
  }

  // No permission or no valid link -> render as button with onClick handler
  return (
    <button
      onClick={() => onClick(shoe)}
      className={buttonClasses}
    >
      {size === 'medium' && <span>Buy Now</span>}
      {size === 'small' && 'Buy Now'}
    </button>
  );
};

const RecommendationCard = ({ recommendation, isLarge = false, rank, onBuyClick, onFavoriteClick, isFavorite = false, hasBuyNowPermission = false }: RecommendationCardProps) => {
  const { shoe } = recommendation;

  const getRankLabel = (position: number) => {
    const labels = [
      'Best Overall',
      'Great Value',
      'Top Rated',
      'Smart Choice',
      'Quality Pick',
      'Solid Option',
      'Good Deal',
      'Worth Considering'
    ];
    return labels[position - 1] || `Choice #${position}`;
  };

  // Generate shoe detail page URL
  const getShoeDetailUrl = () => {
    // Use shoe._id if available, otherwise generate from brand and model
    if (shoe._id) {
      return `/shoe/${shoe._id}`;
    }
    // Fallback: generate ID from brand and model
    const modelName = shoe.model.startsWith(shoe.brand) ? shoe.model : `${shoe.brand} ${shoe.model}`;
    const shoeId = modelName.replace(/\s+/g, '-');
    return `/shoe/${shoeId}`;
  };

  // Handle click to open shoe detail page in new tab
  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent navigation if clicking on buttons
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) {
      return;
    }
    // Open in new tab
    window.open(getShoeDetailUrl(), '_blank', 'noopener,noreferrer');
  };
  
  if (isLarge) {
    return (
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-2.5 sm:p-4 border border-gray-100 h-full">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <span className="bg-orange-100 text-orange-800 text-xs sm:text-sm font-semibold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full">
                #{rank}
              </span>
              <span className="text-gray-500 text-[10px] sm:text-xs hidden sm:inline">{getRankLabel(rank)}</span>
            </div>
            <button
              onClick={() => onFavoriteClick(shoe)}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors group"
              title={isFavorite ? "Remove from Favorites" : "Add to Favorites"}
            >
              <Heart className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors ${
                isFavorite
                  ? 'text-red-500 fill-current'
                  : 'text-gray-400 group-hover:text-red-500'
              }`} />
            </button>
          </div>

          {/* Clickable Image */}
          <div
            onClick={handleCardClick}
            className="cursor-pointer"
          >
            <img
              src={shoe.image || '/placeholder-shoe.jpg'}
              alt={`${shoe.brand} ${shoe.model}`}
              className="w-full h-28 sm:h-40 object-cover rounded-lg mb-2 sm:mb-4 hover:opacity-90 transition-opacity"
            />
          </div>

          <div className="flex-1 flex flex-col">
            {/* Clickable Title */}
            <div
              onClick={handleCardClick}
              className="cursor-pointer"
            >
              <h3 className="font-bold text-gray-900 text-sm sm:text-lg mb-1.5 sm:mb-2 line-clamp-2 hover:text-orange-600 transition-colors leading-tight">
                {shoe.model}
              </h3>
            </div>

            {/* Clickable Price */}
            <div
              onClick={handleCardClick}
              className="text-right mb-2 sm:mb-3 cursor-pointer"
            >
              <div className="flex items-center justify-end gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                <div className="text-xl sm:text-3xl font-bold text-green-600 hover:text-green-700 transition-colors">${shoe.price.current}</div>
                {shoe.price.discount > 0 && (
                  <span className="inline-block bg-red-500 text-white text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded">
                    -{shoe.price.discount}%
                  </span>
                )}
              </div>
              {shoe.price.discount > 0 && (
                <div className="text-xs sm:text-sm text-gray-400 line-through">
                  ${shoe.price.original}
                </div>
              )}
            </div>

            <div className="mt-auto">
              <BuyButton shoe={shoe} onClick={onBuyClick} size="medium" hasPermission={hasBuyNowPermission} />
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-2 sm:p-3 border border-gray-100 h-full">
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between mb-1.5 sm:mb-2">
          <span className="bg-gray-100 text-gray-700 text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full">
            #{rank}
          </span>
          <button
            onClick={() => onFavoriteClick(shoe)}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors group flex items-center justify-center"
            title={isFavorite ? "Remove from Favorites" : "Add to Favorites"}
          >
            <Heart className={`w-3.5 h-3.5 sm:w-4 sm:h-4 transition-colors ${
              isFavorite
                ? 'text-red-500 fill-current'
                : 'text-gray-400 group-hover:text-red-500'
            }`} />
          </button>
        </div>

        {/* Clickable Image */}
        <div
          onClick={handleCardClick}
          className="cursor-pointer"
        >
          <img
            src={shoe.image || '/placeholder-shoe.jpg'}
            alt={`${shoe.brand} ${shoe.model}`}
            className="w-full h-20 sm:h-24 object-cover rounded-lg mb-2 sm:mb-3 hover:opacity-90 transition-opacity"
          />
        </div>

        <div className="flex-1 flex flex-col">
          {/* Clickable Title */}
          <div
            onClick={handleCardClick}
            className="cursor-pointer"
          >
            <h4 className="font-bold text-gray-900 text-xs sm:text-sm mb-1.5 sm:mb-2 line-clamp-2 leading-tight hover:text-orange-600 transition-colors">
              {shoe.model}
            </h4>
          </div>

          {/* Clickable Price */}
          <div
            onClick={handleCardClick}
            className="mb-2 sm:mb-3 min-h-[40px] sm:min-h-[50px] cursor-pointer"
          >
            <div className="flex items-center gap-1 sm:gap-2 mb-0.5 sm:mb-1">
              <div className="text-base sm:text-xl font-bold text-green-600 hover:text-green-700 transition-colors">${shoe.price.current}</div>
              {shoe.price.discount > 0 && (
                <span className="inline-block bg-red-500 text-white text-[9px] sm:text-xs font-semibold px-1 py-0.5 rounded">
                  -{shoe.price.discount}%
                </span>
              )}
            </div>
            {shoe.price.discount > 0 && (
              <div className="text-[10px] sm:text-sm text-gray-400 line-through">
                ${shoe.price.original}
              </div>
            )}
          </div>

          <div className="mt-auto">
            <BuyButton shoe={shoe} onClick={onBuyClick} size="small" hasPermission={hasBuyNowPermission} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default function EnhancedRecommendationPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // Search state
  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get('search');
  const [searchMode, setSearchMode] = useState(false);
  const [searchResults, setSearchResults] = useState<RunningShoe[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [userProfile, setUserProfile] = useState<Partial<EnhancedUserProfile>>({
    footWidth: 'medium'
  });
  const [recommendations, setRecommendations] = useState<EnhancedRecommendationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingStep, setLoadingStep] = useState<string>('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showQuickAuthModal, setShowQuickAuthModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [selectedShoe, setSelectedShoe] = useState<any>(null);
  const [favoriteStatuses, setFavoriteStatuses] = useState<Record<string, boolean>>({});
  const [showMore, setShowMore] = useState(false);
  const [singleBrandFilter, setSingleBrandFilter] = useState<string | null>(null);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [preFilterRecommendations, setPreFilterRecommendations] = useState<EnhancedRecommendationResponse | null>(null);
  const [recommendationsBrand, setRecommendationsBrand] = useState<string | null>(null);

  const { isAuthenticated, isPremium, refreshProfile, refreshPremiumStatus } = useAuth();
  const {
    userProfile: cachedUserProfile,
    recommendations: cachedRecommendations,
    hasValidCache,
    saveState,
    clearState
  } = useRecommendationState();

  const moreOptionsRef = useRef<HTMLDivElement>(null);
  
  // Frontend rate limiting to prevent rapid requests
  const lastRequestTimeRef = useRef<number>(0);
  const MIN_REQUEST_INTERVAL = 1000; // 1 second in milliseconds

  // CRITICAL FIX: Use localStorage as fallback for authentication check
  // This ensures Buy Now buttons work even if AuthContext hasn't fully initialized yet
  const hasAuthToken = !!localStorage.getItem('auth_token');
  const effectivelyAuthenticated = isAuthenticated || hasAuthToken;
  
  // For BuyButton, user needs to be Premium to get direct link access
  // effectivelyPremium is true only if user is authenticated AND has premium subscription
  const effectivelyPremium = effectivelyAuthenticated && isPremium;

  // Initialize form with default values from form configuration
  useEffect(() => {
    const initialData: any = { footWidth: 'medium' };

    // Collect all default values from form steps
    enhancedFormSteps.forEach(step => {
      step.fields.forEach(field => {
        if (field.defaultValue !== undefined) {
          initialData[field.name] = field.defaultValue;
        }
      });
    });

    setUserProfile(initialData);
  }, []);

  // Restore cached state if available
  useEffect(() => {
    if (hasValidCache && cachedUserProfile && cachedRecommendations) {
      setUserProfile(cachedUserProfile);
      setRecommendations(cachedRecommendations);
    }
  }, [hasValidCache, cachedUserProfile, cachedRecommendations]);

  // Track previous brand filter to detect changes
  const prevBrandFilterRef = useRef<string | null | undefined>(undefined);
  
  // Auto-trigger recommendations when single brand filter changes
  useEffect(() => {
    // Skip on initial mount (when prevBrandFilterRef.current is undefined)
    if (prevBrandFilterRef.current === undefined) {
      prevBrandFilterRef.current = singleBrandFilter;
      return;
    }
    
    // Only trigger if the brand filter actually changed
    if (prevBrandFilterRef.current !== singleBrandFilter && isFormValid()) {
      fetchRecommendations();
    }
    
    // Update the ref for next comparison
    prevBrandFilterRef.current = singleBrandFilter;
  }, [singleBrandFilter]);

  // Check if single brand filter results contain the selected brand
  // Only check brand match if recommendations are fresh (fetched for current brand)
  useEffect(() => {
    if (singleBrandFilter && recommendations && !loading && recommendationsBrand === singleBrandFilter) {
      // Only check brand match if recommendations are fresh (fetched for current brand)
      // Add a small delay to ensure recommendations have fully loaded
      const checkTimer = setTimeout(() => {
        // Check if any recommendation matches the selected brand (case-insensitive)
        const hasBrandMatches = recommendations.recommendations.some(
          rec => rec.shoe.brand?.toLowerCase() === singleBrandFilter.toLowerCase()
        );

        if (!hasBrandMatches) {
          // No shoes found for the selected brand
          const brandCount = recommendations.recommendations.length;
          const availableBrands = [...new Set(recommendations.recommendations.map(r => r.shoe.brand))].join(', ');

          console.log(`⚠️ Single brand filter "${singleBrandFilter}" has no matches`);
          console.log(`📊 Available brands in results: ${availableBrands}`);

          toast.error(
            `No ${singleBrandFilter} shoes found matching your current criteria. Try increasing your budget or adjusting other preferences to see ${singleBrandFilter} options. Showing ${brandCount} best alternatives for now.`,
            {
              duration: 5000, // Reduced from 8000ms to 5000ms
              position: 'top-center',
              style: {
                background: '#FEF3C7',
                color: '#92400E',
                border: '2px solid #F59E0B',
                padding: '16px',
                fontSize: '14px',
                fontWeight: '500',
                maxWidth: '600px'
              }
            }
          );
        }
      }, 500); // 500ms delay to ensure state is fully updated

      return () => clearTimeout(checkTimer);
    }
  }, [singleBrandFilter, recommendations, loading, recommendationsBrand]);

  // Load favorite statuses when recommendations are loaded
  useEffect(() => {
    const loadFavoriteStatuses = async () => {
      if (!recommendations?.recommendations || !isAuthenticated) {
        return;
      }

      const statuses: Record<string, boolean> = {};

      // Check favorite status for each recommended shoe
      for (const rec of recommendations.recommendations) {
        try {
          const modelName = rec.shoe.model.startsWith(rec.shoe.brand) ? rec.shoe.model : `${rec.shoe.brand} ${rec.shoe.model}`;
          const shoeId = modelName.replace(/\s+/g, '-');
          const status = await checkFavoriteStatus(shoeId);
          statuses[shoeId] = status.isFavorite;
        } catch (error) {
          // Silently fail - not critical
          const fallbackModelName = rec.shoe.model.startsWith(rec.shoe.brand) ? rec.shoe.model : `${rec.shoe.brand} ${rec.shoe.model}`;
          const fallbackShoeId = fallbackModelName.replace(/\s+/g, '-');
          statuses[fallbackShoeId] = false;
        }
      }

      setFavoriteStatuses(statuses);
    };

    loadFavoriteStatuses();
  }, [recommendations, isAuthenticated]);

  // Search mode: Monitor URL changes and fetch search results
  useEffect(() => {
    const fetchSearchResults = async (query: string) => {
      setSearchLoading(true);
      try {
        const response = await searchShoes(query, 20);
        if (response.success && response.data) {
          setSearchResults(response.data);
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults([]);
        toast.error('Search failed, please try again');
      } finally {
        setSearchLoading(false);
      }
    };

    if (searchQuery && searchQuery.length >= 2) {
      setSearchMode(true);
      fetchSearchResults(searchQuery);
    } else {
      setSearchMode(false);
      setSearchResults([]);
    }
  }, [searchQuery]);

  // Load favorite statuses for search results
  useEffect(() => {
    const loadSearchFavorites = async () => {
      if (!searchResults.length || !isAuthenticated) {
        return;
      }

      const statuses: Record<string, boolean> = {};

      for (const shoe of searchResults) {
        try {
          const status = await checkFavoriteStatus(shoe._id);
          statuses[shoe._id] = status.isFavorite;
        } catch (error) {
          statuses[shoe._id] = false;
        }
      }

      setFavoriteStatuses(statuses);
    };

    loadSearchFavorites();
  }, [searchResults, isAuthenticated]);

  // Helper function: Convert RunningShoe to EnhancedRecommendation format
  const mapShoeToRecommendation = (shoe: RunningShoe, rank: number): EnhancedRecommendation => {
    return {
      rank,
      shoe: {
        _id: shoe._id,
        brand: shoe.brand,
        model: shoe.model,
        rating: shoe.ratings?.overall || 0,
        price: {
          original: shoe.price?.original || 0,
          current: shoe.price?.current || 0,
          discount: shoe.price?.discount || 0,
          currency: 'USD'
        },
        image: (shoe.images && shoe.images.length > 0) ? shoe.images[0] : '/placeholder-shoe.jpg',
        purchaseLink: shoe.availability?.url || '#',
        specifications: {
          weight: shoe.specifications?.weight?.toString() || '',
          drop: shoe.specifications?.drop?.toString() || '',
          widthsAvailable: 'Standard'
        }
      },
      scores: {
        total: 0,
        coreMatch: 0,
        valueForMoney: 0,
        quality: 0,
        sceneBonus: 0,
        bigBrandBonus: 0,
        preferenceBonus: 0
      },
      reasoning: '',
      tags: []
    };
  };

  const handleFormChange = (field: string, value: any) => {
    setUserProfile(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear cache when user changes form values to prevent stale cached results
    // This ensures fresh recommendations when parameters change
    if (recommendations) {
      clearState();
    }
  };

  const handleShowMore = () => {
    setShowMore(true);

    // Smooth scroll to the expanded content after a brief delay
    setTimeout(() => {
      moreOptionsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  const handleShowLess = () => {
    setShowMore(false);
    // Scroll back to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Popular brands to display as radio buttons
  const POPULAR_BRANDS = ['Nike', 'Adidas', 'HOKA', 'Brooks', 'ASICS'];

  const handleBrandSelect = (brand: string | null) => {
    console.log('🔍 Brand selected:', brand || 'All Brands');
    
    // Clear cache to ensure fresh recommendations
    if (recommendations) {
      clearState();
    }
    
    // Clear pre-filter recommendations when changing brand selection
    setPreFilterRecommendations(null);
    
    // Clear brand tracking to prevent false mismatch detection
    setRecommendationsBrand(null);
    
    // Dismiss any existing toasts to prevent stale error messages
    toast.dismiss();
    
    if (brand) {
      // Selected a specific brand
      setSingleBrandFilter(brand);
      setUserProfile(prev => ({
        ...prev,
        singleBrandFilter: brand,
        brandPreferences: [] // Ensure brandPreferences is not sent
      }));
    } else {
      // Selected "All Brands"
      setSingleBrandFilter(null);
      const newProfile = { ...userProfile };
      delete newProfile.singleBrandFilter;
      delete newProfile.brandPreferences;
      setUserProfile(newProfile);
    }
  };

  const handleSelectBrand = (brand: string) => {
    // Called from BrandFilterModal - use unified handler
    handleBrandSelect(brand);
  };

  const handleClearBrandFilter = async () => {
    console.log('🧹 Clearing brand filter...');
    setSingleBrandFilter(null);

    // Remove singleBrandFilter from userProfile
    const newProfile = { ...userProfile };
    delete newProfile.singleBrandFilter;
    setUserProfile(newProfile);

    // Clear cache to prevent old results from being restored
    clearState();

    // Restore pre-filter recommendations if available, otherwise re-fetch
    if (preFilterRecommendations) {
      console.log('🔄 Restoring pre-filter recommendations');
      setRecommendations(preFilterRecommendations);
      setPreFilterRecommendations(null);
      // Save the restored state to cache
      saveState(newProfile as EnhancedUserProfile, preFilterRecommendations);
    } else {
      // No pre-filter recommendations saved, need to re-fetch
      console.log('🔄 No pre-filter recommendations found, re-fetching with profile:', newProfile);

      // Clear current recommendations to show loading state
      setRecommendations(null);
      setLoading(true);
      setLoadingStep('Removing brand filter...');

      try {
        // Fetch recommendations with the new profile (without singleBrandFilter)
        setTimeout(async () => {
          try {
            console.log('📡 Fetching recommendations without brand filter...');
            const response = await api.getEnhancedRecommendations(newProfile as EnhancedUserProfile);

            setRecommendations(response);
            // Save state for recovery
            saveState(newProfile as EnhancedUserProfile, response);
            setLoading(false);
            setLoadingStep('');
          } catch (error) {
            console.error('Error fetching recommendations:', error);
            setLoading(false);
            setError('Failed to fetch recommendations. Please try again.');
          }
        }, 100);
      } catch (error) {
        console.error('Error in handleClearBrandFilter:', error);
        setLoading(false);
      }
    }
  };

  const fetchRecommendations = async () => {
    // Frontend rate limiting: prevent rapid requests (max 1 per second)
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTimeRef.current;
    
    if (lastRequestTimeRef.current > 0 && timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
      // Too fast! Show friendly reminder and stop
      toast.error('Please slow down! Wait a moment before trying again.', {
        duration: 2000,
        icon: '⏱️',
        style: {
          background: '#FEF3C7',
          color: '#92400E',
          border: '2px solid #F59E0B',
        }
      });
      return;
    }
    
    // Update last request time
    lastRequestTimeRef.current = now;

    if (!isFormValid()) {
      setValidationError('Please complete the required fields (marked with *)');
      return;
    }
    setValidationError(null);

    // Reset showMore state when fetching new recommendations
    setShowMore(false);

    // Clear pre-filter recommendations when manually fetching
    setPreFilterRecommendations(null);

    // Note: Usage check is now handled by backend (IP-based for anonymous, user-based for logged in)
    // No need to pre-check here - let the API call handle it

    setLoading(true);
    setError(null);
    setLoadingStep('Loading product database...');
    
    // Clear previous brand tracking to prevent false mismatch detection
    setRecommendationsBrand(null);
    
    // Dismiss any existing toasts to prevent stale error messages
    toast.dismiss();

    try {
      // Simulate progressive loading steps
      setTimeout(() => setLoadingStep('Applying filters based on your preferences...'), 500);
      setTimeout(() => setLoadingStep('Analyzing matches and calculating scores...'), 1500);
      setTimeout(() => setLoadingStep('Generating AI-powered personalized reasons...'), 3000);

      const response = await api.getEnhancedRecommendations(userProfile as EnhancedUserProfile);
      setRecommendations(response);
      
      // Track which brand these recommendations are for (null if no brand filter)
      setRecommendationsBrand(userProfile.singleBrandFilter || null);

      // Save state for recovery
      saveState(userProfile as EnhancedUserProfile, response);

      // Trigger usage update event for Layout component to refresh remaining count
      window.dispatchEvent(new CustomEvent('usageUpdated'));

      setLoadingStep('');
    } catch (err: any) {
      console.error('Error fetching recommendations:', err);

      // Check if this is a "no results found" error (404 status)
      if (err.response?.status === 404) {
        const errorData = err.response.data;
        const suggestions = errorData.suggestions || [];

        // Format error message with suggestions
        let errorMessage = 'No shoes found matching your criteria. ';
        if (suggestions.length > 0) {
          errorMessage += 'Suggestions:\n• ' + suggestions.join('\n• ');
        }

        setError(errorMessage);
        toast.error('No matching shoes found', {
          duration: 6000,
          icon: '🔍'
        });
      }
      // Handle rate limiting (429 - too many requests)
      else if (err.response?.status === 429) {
        const retryAfter = err.response.data?.retryAfter || 1;
        setError(`Please wait a moment before requesting again. Try again in ${retryAfter} second(s).`);
        toast.error('Too many requests. Please slow down.', {
          duration: 3000,
          icon: '⏳'
        });
      } else {
        // Generic error - provide a user-friendly message
        setError('Something went wrong. Please wait a moment and try again.');
        toast.error('Please wait a moment and try again.', {
          duration: 3000,
          icon: '⏱️'
        });
      }

      setLoadingStep('');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    // Check if all required fields have valid values (regardless of how they were set)
    const hasGender = userProfile.gender && (userProfile.gender === 'male' || userProfile.gender === 'female');
    const hasBudget = userProfile.budget && typeof userProfile.budget === 'number' && userProfile.budget > 0;
    const hasPurpose = userProfile.purpose && ['daily-training', 'racing', 'trail', 'casual'].includes(userProfile.purpose);
    
    return hasGender && hasBudget && hasPurpose;
  };

  const handleBuyClick = async (shoe: any) => {
    console.log('🔵 [BUY NOW] handleBuyClick called with shoe:', shoe);
    setSelectedShoe(shoe);

    // Validate purchase link
    if (!shoe.purchaseLink || shoe.purchaseLink === '' || shoe.purchaseLink === '#') {
      toast.error('Purchase link is not available for this shoe.');
      return;
    }

    // Check permission via API to get accurate needsAuth/needsPayment status
    try {
      const permission = await checkBuyNowPermission();

      if (permission.hasPermission) {
        // User is Premium - should not reach here if BuyButton rendered as <a>
        // But as a fallback, open the link directly
        window.open(shoe.purchaseLink, '_blank');
        return;
      }

      if (permission.needsAuth) {
        // User not logged in - show auth modal
        setShowQuickAuthModal(true);
        return;
      }

      if (permission.needsPayment) {
        // User logged in but not Premium - show upgrade modal
        setShowPremiumModal(true);
        return;
      }
    } catch (error) {
      console.error('Permission check failed:', error);
      // Fallback to auth modal
      setShowQuickAuthModal(true);
    }
  };

  const handleFavoriteClick = async (shoe: any) => {
    if (!isAuthenticated) {
      // Show sign-in modal for unauthenticated users
      setShowQuickAuthModal(true);
      return;
    }
    
    // For authenticated users, check if they have premium
    try {
      const permission = await api.checkBuyNowPermission();
      
      if (permission.hasPermission) {
        // User has premium - toggle favorite status
        const modelName = shoe.model.startsWith(shoe.brand) ? shoe.model : `${shoe.brand} ${shoe.model}`;
        const shoeId = modelName.replace(/\s+/g, '-');
        const currentlyFavorite = favoriteStatuses[shoeId] || false;
        
        try {
          if (currentlyFavorite) {
            // Remove from favorites
            await removeFavorite(shoeId);
            setFavoriteStatuses(prev => ({ ...prev, [shoeId]: false }));
            toast.success('Removed from favorites');
          } else {
            // Add to favorites
            // 将性别信息保存到 shoeBrand 字段（用于在收藏列表中显示）
            const genderLabel = userProfile.gender === 'male' ? 'Men' :
                               userProfile.gender === 'female' ? 'Women' :
                               shoe.brand; // 如果没有性别信息，回退到品牌名

            await addFavorite({
              shoeId: shoeId,
              shoeName: shoe.model || shoe.name,
              shoeBrand: genderLabel,  // 保存性别而不是品牌
              shoeImage: shoe.image || '/placeholder-shoe.jpg',
              shoeUrl: shoe.purchaseLink || '#',
              currentPrice: shoe.price?.current || shoe.price,
              originalPrice: shoe.price?.original || shoe.price,
              discountPercentage: shoe.price?.discount || 0
            });
            setFavoriteStatuses(prev => ({ ...prev, [shoeId]: true }));
            toast.success('Added to favorites');
          }
        } catch (favoriteError: any) {
          console.error('Error toggling favorite:', favoriteError);
          toast.error(favoriteError.message || 'Failed to update favorites');
        }
      } else {
        // Show payment modal for free users
        setShowPaymentModal(true);
      }
    } catch (error) {
      console.error('Error checking favorite permission:', error);
      setShowPaymentModal(true);
    }
  };

  const handlePaymentComplete = async () => {
    setShowPaymentModal(false);
    
    // Refresh user profile to get updated usage info
    try {
      await refreshProfile();
    } catch (error) {
      console.error('Failed to refresh profile after payment:', error);
    }
    
    // After payment, redirect to purchase link
    if (selectedShoe?.purchaseLink) {
      window.open(selectedShoe.purchaseLink, '_blank', 'noopener noreferrer');
    }
    
    // Show success message
    alert('Payment successful! You now have access to purchase links.');
    setSelectedShoe(null);
  };

  const handleActivationSuccess = async () => {
    setShowPaymentModal(false);
    
    // Refresh user profile to get updated usage info
    try {
      await refreshProfile();
    } catch (error) {
      console.error('Failed to refresh profile after activation:', error);
    }
    
    // Show success message and clear selected shoe
    alert('Activation successful! You now have 30-day premium access to discount links.');
    setSelectedShoe(null);
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
    setSelectedShoe(null);
  };

  const handleQuickAuthSuccess = async () => {
    setShowQuickAuthModal(false);
    
    // Just close the modal - the AuthContext should already be updated
    // The user can click Buy Now again if needed
    setSelectedShoe(null);
    
    // Optional: show a success message or notification
    console.log('Authentication successful!');
  };

  const handleQuickAuthCancel = () => {
    setShowQuickAuthModal(false);
    setSelectedShoe(null);
  };

  // Helper function to render a form field with its label
  const renderFieldContainer = (field: any) => (
    <div className="border border-gray-200 rounded-lg p-2 sm:p-3">
      <div className="space-y-0.5 sm:space-y-2">
        <label className="block text-xs sm:text-sm font-medium text-gray-700">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className="min-h-[40px] sm:min-h-[60px]">
          {renderFormField(field)}
        </div>
        {field.helpText && (
          <p className="text-xs text-gray-500 hidden sm:block">{field.helpText}</p>
        )}
      </div>
    </div>
  );

  const renderFormField = (field: any) => {
    switch (field.type) {
      case 'radio':
        // Purpose field gets responsive grid layout
        if (field.name === 'purpose') {
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {field.options.map((option: any) => (
                <label key={option.value} className="flex items-center space-x-3 cursor-pointer group p-3 border border-gray-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-all min-h-[44px]">
                  <input
                    type="radio"
                    name={field.name}
                    value={option.value}
                    checked={userProfile[field.name as keyof EnhancedUserProfile] === option.value}
                    onChange={(e) => handleFormChange(field.name, e.target.value)}
                    className="w-5 h-5 text-orange-600 border-gray-300 focus:ring-orange-500"
                  />
                  <div className="font-medium text-gray-900 group-hover:text-orange-600 transition-colors text-base sm:text-sm">
                    {option.label}
                  </div>
                </label>
              ))}
            </div>
          );
        }
        
        // Other radio fields keep vertical layout
        return (
          <div className="space-y-3">
            {field.options.map((option: any) => (
              <label key={option.value} className="flex items-center space-x-3 cursor-pointer group">
                <input
                  type="radio"
                  name={field.name}
                  value={option.value}
                  checked={userProfile[field.name as keyof EnhancedUserProfile] === option.value}
                  onChange={(e) => handleFormChange(field.name, e.target.value)}
                  className="w-4 h-4 text-orange-600 border-gray-300 focus:ring-orange-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 group-hover:text-orange-600 transition-colors">
                    {option.label}
                  </div>
                  {option.description && (
                    <div className="text-sm text-gray-500">{option.description}</div>
                  )}
                </div>
              </label>
            ))}
          </div>
        );
      
      case 'range':
        const currentBudget = userProfile.budget || field.defaultValue;
        const percentage = ((currentBudget - field.min) / (field.max - field.min)) * 100;
        return (
          <div className="py-2 sm:py-0">
            <div className="relative mb-2 sm:mb-2">
              <div 
                className="absolute -top-2 sm:top-0 text-base sm:text-lg font-semibold text-gray-900 transform -translate-x-1/2 z-10"
                style={{ left: `${percentage}%` }}
              >
                ${currentBudget}
              </div>
              <div className="flex justify-between text-xs sm:text-sm text-gray-400 pt-2 sm:pt-0">
                <span>$50</span>
                <span>$300</span>
              </div>
            </div>
            <div className="relative">
              <input
                type="range"
                min={field.min}
                max={field.max}
                step={field.step}
                value={currentBudget}
                onChange={(e) => handleFormChange(field.name, parseInt(e.target.value))}
                className="w-full"
                style={{ touchAction: 'pan-y' }}
              />
            </div>
          </div>
        );
      
      case 'multiselect':
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {field.options.map((option: any) => (
              <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={userProfile.brandPreferences?.includes(option.value) || false}
                  onChange={(e) => {
                    const current = userProfile.brandPreferences || [];
                    const updated = e.target.checked
                      ? [...current, option.value]
                      : current.filter(brand => brand !== option.value);
                    handleFormChange('brandPreferences', updated);
                  }}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        );
      
      case 'dropdown':
        return (
          <select
            value={userProfile[field.name as keyof EnhancedUserProfile] || field.defaultValue}
            onChange={(e) => handleFormChange(field.name, e.target.value)}
            className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white text-sm sm:text-base"
          >
            {field.options.map((option: any) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      
      case 'textarea':
        return (
          <textarea
            placeholder={field.placeholder}
            value={userProfile.personalDescription || ''}
            onChange={(e) => handleFormChange(field.name, e.target.value)}
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white sm:bg-gradient-to-br sm:from-orange-50 sm:to-blue-50 overflow-x-hidden">
      <div className="w-full mx-auto px-2 sm:px-6 lg:px-8 py-2 sm:py-4">
        {/* User Input Form - Always visible */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-2 sm:p-6 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-6 flex items-center gap-2">
            <Target className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
                Your Preferences
              </h2>
          
          {/* Basic Fields - Gender and Budget side-by-side on mobile */}
          <div className="space-y-2 sm:space-y-0 mb-3 sm:mb-6">
            {/* First row: Gender + Budget (mobile: 2 cols, desktop: 2 cols) */}
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-2 sm:gap-6">
              {/* Gender field */}
              {(() => {
                const field = enhancedFormSteps[0].fields.find(f => f.name === 'gender');
                return field ? renderFieldContainer(field) : null;
              })()}
              
              {/* Budget field */}
              {(() => {
                const field = enhancedFormSteps[0].fields.find(f => f.name === 'budget');
                return field ? renderFieldContainer(field) : null;
              })()}
            </div>
            
            {/* Second row: Purpose + Foot Width (mobile: 2 cols side-by-side, desktop: 2 cols side-by-side) */}
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-2 sm:gap-6">
              {/* Purpose field */}
              {(() => {
                const field = enhancedFormSteps[0].fields.find(f => f.name === 'purpose');
                return field ? renderFieldContainer(field) : null;
              })()}
              
              {/* Foot Width field - Moved out of Advanced Options, same row as Purpose */}
              {enhancedFormSteps[1].fields.map((field) => (
                <div key={field.name}>
                  {renderFieldContainer(field)}
                </div>
              ))}
            </div>
          </div>

          {/* Brand Filter - Moved out of Advanced Options, always visible */}
          <div className="space-y-0.5 sm:space-y-2 mb-3 sm:mb-6">
            <div className="border border-gray-200 rounded-lg p-2 sm:p-3">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                Select Brand (Optional)
              </label>
              <div className="min-h-[40px] sm:min-h-[60px]">
                <div className="flex flex-wrap gap-1.5 sm:gap-2 items-center">
                {/* All Brands option */}
                <label 
                  className={`flex items-center justify-center px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-lg cursor-pointer transition-all duration-200 border-2 bg-white ${
                    !singleBrandFilter
                      ? 'border-orange-600 text-gray-900'
                      : 'border-gray-200 text-gray-700 hover:border-orange-300 hover:bg-orange-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="brandFilter"
                    checked={!singleBrandFilter}
                    onChange={() => handleBrandSelect(null)}
                    className="sr-only"
                  />
                  <span className="text-xs sm:text-sm font-medium">All Brands</span>
                </label>
                
                {/* Popular brands */}
                {POPULAR_BRANDS.map(brand => (
                  <label 
                    key={brand}
                    className={`flex items-center justify-center px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-lg cursor-pointer transition-all duration-200 border-2 bg-white ${
                      singleBrandFilter === brand
                        ? 'border-orange-600 text-gray-900'
                        : 'border-gray-200 text-gray-700 hover:border-orange-300 hover:bg-orange-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="brandFilter"
                      checked={singleBrandFilter === brand}
                      onChange={() => handleBrandSelect(brand)}
                      className="sr-only"
                    />
                    <span className="text-xs sm:text-sm font-medium">{brand}</span>
                  </label>
                ))}
                
                {/* More Brands button - Now in the same flex row, styled like other brand buttons */}
                <button
                  type="button"
                  onClick={() => setShowBrandModal(true)}
                  className="flex items-center justify-center gap-1 px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-lg cursor-pointer transition-all duration-200 border-2 bg-white border-gray-200 text-gray-700 hover:border-orange-300 hover:bg-orange-50 text-xs sm:text-sm font-medium"
                >
                  <span>More Brands</span>
                  <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
              </div>
              
                {/* Show selected brand if it's not in popular list */}
                {singleBrandFilter && !POPULAR_BRANDS.includes(singleBrandFilter) && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-sm text-gray-600">Selected:</span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-lg text-sm font-medium">
                      {singleBrandFilter}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleBrandSelect(null)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Clear selection"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          
          {/* Get Recommendations Button */}
          <div className="flex flex-col items-center gap-2 sm:gap-3">
            <button
              onClick={fetchRecommendations}
              disabled={loading}
              className={`w-full sm:w-auto py-2.5 px-6 sm:py-4 sm:px-8 rounded-lg sm:rounded-xl font-semibold text-base sm:text-lg transition-all duration-300 ${
                !loading
                  ? 'bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="inline-block">
                    <RunningShoeLoader className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <span className="text-sm sm:text-base">Finding Your Perfect Shoes...</span>
                </div>
              ) : (
                'Find My Perfect Shoes'
              )}
            </button>
          </div>
          {validationError && (
            <p className="text-sm text-red-600 mt-3 text-center font-medium">
              {validationError}
            </p>
          )}
        </div>

        {/* Loading State - Only show when not in search mode */}
        {!searchMode && loading && (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="flex justify-center mb-6">
              <RunningShoeLoader className="w-24 h-24" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">
              Finding Your Perfect Shoes
            </h3>
            <div className="bg-orange-50 rounded-lg p-4 mb-4">
              <p className="text-orange-800 font-medium">
                {loadingStep || 'Initializing recommendation engine...'}
              </p>
            </div>
            <div className="text-sm text-gray-500 space-y-1">
              <p>✓ Analyzing 500+ {userProfile.purpose || 'running'} shoes</p>
              <p>✓ Filtering by your ${userProfile.budget || 150} budget</p>
              <p>✓ Matching {userProfile.gender || 'your'} preferences</p>
              <p>⏳ Generating personalized AI reasoning...</p>
            </div>
          </div>
        )}

        {/* Error State - Only show when not in search mode */}
        {!searchMode && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        )}

        {/* Recommendations Results - Only show when not in search mode */}
        {!searchMode && recommendations && !loading && (
          <div className="space-y-3 sm:space-y-6">
            {/* Mobile View: 2+2+2+2 (4 rows, 2 cards each) */}
            <div className="block sm:hidden">
              <div className="grid grid-cols-2 gap-2">
                {recommendations.recommendations.slice(0, 8).map((rec, index) => (
                  <RecommendationCard
                    key={`${rec.shoe.brand}-${rec.shoe.model}`}
                    recommendation={rec}
                    isLarge={false}
                    rank={index + 1}
                    onBuyClick={handleBuyClick}
                    onFavoriteClick={handleFavoriteClick}
                    isFavorite={favoriteStatuses[(rec.shoe.model.startsWith(rec.shoe.brand) ? rec.shoe.model : `${rec.shoe.brand} ${rec.shoe.model}`).replace(/\s+/g, '-')] || false}
                    hasBuyNowPermission={effectivelyPremium}
                  />
                ))}
              </div>
            </div>

            {/* PC View: 3+5 (first row 3 cards, second row 5 cards) */}
            <div className="hidden sm:block">
              {/* Top 3 - Large Cards */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {recommendations.recommendations.slice(0, 3).map((rec, index) => (
                  <RecommendationCard
                    key={`${rec.shoe.brand}-${rec.shoe.model}`}
                    recommendation={rec}
                    isLarge={true}
                    rank={index + 1}
                    onBuyClick={handleBuyClick}
                    onFavoriteClick={handleFavoriteClick}
                    isFavorite={favoriteStatuses[(rec.shoe.model.startsWith(rec.shoe.brand) ? rec.shoe.model : `${rec.shoe.brand} ${rec.shoe.model}`).replace(/\s+/g, '-')] || false}
                    hasBuyNowPermission={effectivelyPremium}
                  />
                ))}
              </div>

              {/* Next 5 - Small Cards (Ranks 4-8) */}
              {recommendations.recommendations.length > 3 && (
                <div className="grid grid-cols-5 gap-4">
                  {recommendations.recommendations.slice(3, 8).map((rec, index) => (
                    <RecommendationCard
                      key={`${rec.shoe.brand}-${rec.shoe.model}`}
                      recommendation={rec}
                      isLarge={false}
                      rank={index + 4}
                      onBuyClick={handleBuyClick}
                      onFavoriteClick={handleFavoriteClick}
                      isFavorite={favoriteStatuses[(rec.shoe.model.startsWith(rec.shoe.brand) ? rec.shoe.model : `${rec.shoe.brand} ${rec.shoe.model}`).replace(/\s+/g, '-')] || false}
                      hasBuyNowPermission={effectivelyPremium}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Show More Prompt Card - Always visible if more results available */}
            {recommendations.recommendations.length > 8 && !showMore && (
              <div className="bg-gradient-to-r from-orange-50 to-blue-50 rounded-lg sm:rounded-xl p-4 sm:p-6 text-center border-2 border-dashed border-orange-200">
                <p className="text-gray-700 text-sm sm:text-base mb-3 sm:mb-4 font-medium">
                  😊 Found {recommendations.recommendations.length - 8} more options
                </p>
                <button
                  onClick={handleShowMore}
                  className="bg-orange-600 hover:bg-orange-700 active:bg-orange-800 text-white font-semibold py-2.5 px-6 sm:py-3 sm:px-8 rounded-lg transition-all duration-200 w-full sm:w-auto shadow-md hover:shadow-lg transform hover:-translate-y-0.5 text-sm sm:text-base"
                >
                  Show {recommendations.recommendations.length - 8} More
                </button>
              </div>
            )}

            {/* Extended Results - Ranks 9-18 */}
            {showMore && recommendations.recommendations.length > 8 && (
              <div ref={moreOptionsRef} className="space-y-4">
                {/* Divider with Title */}
                <div className="flex items-center gap-4 py-4">
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                  <h3 className="text-lg font-semibold text-gray-600">More Options for You</h3>
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                </div>

                {/* Extended Small Cards Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {recommendations.recommendations.slice(8).map((rec, index) => (
                    <RecommendationCard
                      key={`${rec.shoe.brand}-${rec.shoe.model}`}
                      recommendation={rec}
                      isLarge={false}
                      rank={index + 9}
                      onBuyClick={handleBuyClick}
                      onFavoriteClick={handleFavoriteClick}
                      isFavorite={favoriteStatuses[(rec.shoe.model.startsWith(rec.shoe.brand) ? rec.shoe.model : `${rec.shoe.brand} ${rec.shoe.model}`).replace(/\s+/g, '-')] || false}
                      hasBuyNowPermission={effectivelyPremium}
                    />
                  ))}
                </div>

                {/* Show Less Button */}
                <div className="flex justify-center pt-6">
                  <button
                    onClick={handleShowLess}
                    className="text-orange-600 hover:text-orange-700 font-semibold py-2 px-6 rounded-lg hover:bg-orange-50 transition-all duration-200 flex items-center gap-2"
                  >
                    <span>↑</span>
                    <span>Show Less</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Initial State - Only show when not in search mode */}
        {!searchMode && !recommendations && !loading && !error && (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <Target className="h-16 w-16 text-orange-300 mx-auto mb-6" />
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Complete the form above to get your personalized recommendations
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-500 max-w-2xl mx-auto">
              <div className="flex items-center gap-2 justify-center sm:justify-start">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span>AI-powered</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span>Real-time prices</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span>500+ shoes</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span>Best deals first</span>
              </div>
            </div>
          </div>
        )}

        {/* ========== SEARCH RESULTS SECTION ========== */}
        {searchMode && (
          <div className="space-y-3 sm:space-y-6">
            {/* Search Header - Compact on mobile */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-6">
              <div className="flex items-center justify-between flex-wrap gap-2 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <h2 className="text-base sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2 truncate">
                    <span className="hidden sm:inline">Search Results: </span>"{searchQuery}"
                  </h2>
                  <p className="text-xs sm:text-base text-gray-600">
                    Found {searchResults.length} running shoe{searchResults.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center gap-1 sm:gap-2 text-orange-600 hover:text-orange-700 font-semibold py-1.5 sm:py-2 px-2 sm:px-4 rounded-lg border-2 border-orange-300 hover:border-orange-400 hover:bg-orange-50 transition-all text-xs sm:text-sm flex-shrink-0"
                >
                  <X className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Clear Search</span>
                  <span className="sm:hidden">Clear</span>
                </button>
              </div>
            </div>

            {/* Search Loading State */}
            {searchLoading && (
              <div className="flex justify-center py-12">
                <div className="flex flex-col items-center gap-4">
                  <RunningShoeLoader className="w-16 h-16" />
                  <p className="text-gray-600 font-medium">Searching...</p>
                </div>
              </div>
            )}

            {/* Search Results Grid - 2 columns on mobile, 3+ on desktop */}
            {!searchLoading && searchResults.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
                {searchResults.map((shoe, index) => (
                  <RecommendationCard
                    key={shoe._id}
                    recommendation={mapShoeToRecommendation(shoe, index + 1)}
                    rank={index + 1}
                    isLarge={false}
                    onBuyClick={handleBuyClick}
                    onFavoriteClick={handleFavoriteClick}
                    isFavorite={favoriteStatuses[shoe._id]}
                    hasBuyNowPermission={effectivelyPremium}
                  />
                ))}
              </div>
            )}

            {/* Search Empty State */}
            {!searchLoading && searchResults.length === 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <div className="text-6xl mb-4">👟</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  No shoes found for "{searchQuery}"
                </h3>
                <p className="text-gray-600 mb-6">
                  Try different keywords or use the form above to get personalized recommendations
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  Clear Search
                </button>
              </div>
            )}
          </div>
        )}

        {/* Payment Modal */}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={handlePaymentCancel}
          onPayment={handlePaymentComplete}
          onActivationSuccess={handleActivationSuccess}
        />

        {/* Quick Auth Modal */}
        <QuickAuthModal
          isOpen={showQuickAuthModal}
          onClose={handleQuickAuthCancel}
          onSuccess={handleQuickAuthSuccess}
          onUpgradeToPremium={() => setShowPremiumModal(true)}
          title="Sign in to access premium discount links"
        />

        {/* Premium Upgrade Modal */}
        <PremiumUpgradeModal
          isOpen={showPremiumModal}
          onClose={() => {
            setShowPremiumModal(false);
            setSelectedShoe(null);
          }}
          onSuccess={() => {
            setShowPremiumModal(false);
            // After successful payment, user will be redirected to success page
            // Premium status will be updated
          }}
          selectedShoeId={selectedShoe?._id}
          selectedShoeName={selectedShoe?.model ? `${selectedShoe?.brand} ${selectedShoe?.model}` : undefined}
        />

        {/* Brand Filter Modal */}
        <BrandFilterModal
          isOpen={showBrandModal}
          onClose={() => setShowBrandModal(false)}
          onSelectBrand={handleSelectBrand}
        />
      </div>
    </div>
  );
}